import apiClient from "./api-client";

const multipartApiClient = apiClient({ baseURL: "/" });
const LOCAL_STORAGE_KEY = "multipart_upload_state";
// FIX 20260727-empty0 中断記録の有効期限（これを過ぎた記録は無効として捨てる）
const STALE_STATE_MS = 24 * 60 * 60 * 1000;

// ---- 中断機構（中止ボタンからのみ発火。未発火時は挙動不変） ----
let _abortRequested = false;
const _activeXhrs = new Set();
// FIX 20260901-samename ★このタブで送信中のファイル名を数える（同名でも動いているものは片付けない）
const _uploadingFiles = new Map();
const _markUploading = (name) => {
  if (!name) return;
  _uploadingFiles.set(name, (_uploadingFiles.get(name) || 0) + 1);
};
const _unmarkUploading = (name) => {
  if (!name) return;
  const n = (_uploadingFiles.get(name) || 0) - 1;
  if (n > 0) _uploadingFiles.set(name, n);
  else _uploadingFiles.delete(name);
};
const _isUploadingNow = (name) => !!name && (_uploadingFiles.get(name) || 0) > 0;
// FIX 20260901-samename2 ★タブをまたいで「送信中」を見分ける（localStorageは全タブ共有）
const LIVE_STATE_MS = 60 * 1000;   // 印がこれより新しければ「まだ生きている」
const ALIVE_BEAT_MS = 5 * 1000;    // 送信中は5秒ごとに印を押し直す
const _isStateAlive = (state) => {
  if (!state || !state.alive_at) return false;
  const t = Number(state.alive_at);
  return !!t && !isNaN(t) && Date.now() - t < LIVE_STATE_MS;
};
export const requestMultipartAbort = () => {
  _abortRequested = true;
  _activeXhrs.forEach((x) => { try { x.abort(); } catch (e) {} });
  _activeXhrs.clear();
};

/**
 * Check chunk size optimal based on network speed
 * @returns {number} - Suitable chunk size (byte)
 */
const getOptimalChunkSize = () => {
  // Default chunk size is 10MB
  const DEFAULT_CHUNK_SIZE = 10 * 1024 * 1024;

  // Try to get connection information from Navigator API
  if (navigator.connection) {
    const { effectiveType, downlink } = navigator.connection;

    // Adjust chunk size based on network conditions
    if (effectiveType === "4g" && downlink > 5) {
      return 50 * 1024 * 1024; // 50MB for good connection
    } else if (effectiveType === "4g" || effectiveType === "3g") {
      return 10 * 1024 * 1024; // 10MB for average connection
    } else {
      return 5 * 1024 * 1024; // 5MB for slow connection
    }
  }

  return DEFAULT_CHUNK_SIZE;
};

/**
 * Initialize multipart upload on S3
 * @param {File} file - File to upload
 * @returns {Promise<{upload_id: string, key: string}>} - Upload ID and key
 */
export const initMultipartUpload = async (file, allowAbortExisting = true) => {
  try {
    // Abort any existing uploads for this file
    // FIX 20260901-samename 片付けてよいかは呼び出し元が判断する
    const savedState = getUploadState(file.name);
    if (savedState && allowAbortExisting && !_isStateAlive(savedState)) {
      await abortMultipartUpload(savedState.upload_id, savedState.key);
      clearUploadState(file.name);
    }

    const response = await multipartApiClient.post(
      "/akaire_feature/multipart_uploads/initiate",
      {
        objectName: file.name,
        content_type: file.type,
        size: file.size,
      }
    );

    // Save initial state
    saveUploadState(file.name, {
      upload_id: response.data.upload_id,
      key: response.data.key,
      file_size: file.size,
      parts_completed: [],
      started_at: new Date().toISOString(),
    });

    return response.data;
  } catch (error) {
    console.error("Error initializing multipart upload:", error);
    throw error;
  }
};

/**
 * Save upload state to resume when browser refresh
 */
const saveUploadState = (fileName, state) => {
  try {
    const currentState = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEY) || "{}"
    );
    // FIX 20260901-samename2 保存のたびに「生きている印」を押す
    currentState[fileName] = Object.assign({}, state, { alive_at: Date.now() });
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(currentState));
  } catch (e) {
    console.warn("Failed to save upload state:", e);
  }
};

/**
 * Get saved upload state
 */
export const getUploadState = (fileName) => {
  try {
    const currentState = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEY) || "{}"
    );
    const state = currentState[fileName] || null;
    if (!state) return null;
    // FIX 20260727-empty0 古い/壊れた中断記録は無効化（キャッシュ削除しないと直らない事象の対策）
    const startedAt = state.started_at ? Date.parse(state.started_at) : NaN;
    const isStale =
      !startedAt || isNaN(startedAt) || Date.now() - startedAt > STALE_STATE_MS;
    if (isStale || !state.upload_id || state.file_size === 0) {
      delete currentState[fileName];
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(currentState));
      return null;
    }
    return state;
  } catch (e) {
    console.warn("Failed to get upload state:", e);
    return null;
  }
};

/**
 * Clear saved upload state
 */
const clearUploadState = (fileName) => {
  try {
    const currentState = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEY) || "{}"
    );
    delete currentState[fileName];
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(currentState));
  } catch (e) {
    console.warn("Failed to clear upload state:", e);
  }
};

/**
 * Abort multipart upload
 * @param {string} uploadId - Upload ID
 * @param {string} key - S3 key
 */
export const abortMultipartUpload = async (uploadId, key) => {
  try {
    await multipartApiClient.post("/akaire_feature/multipart_uploads/abort", {
      upload_id: uploadId,
      key: key,
    });
    console.log("Multipart upload aborted successfully");
  } catch (error) {
    console.error("Error aborting multipart upload:", error);
  }
};

/**
 * Upload parallel parts of file with retry and save state
 * @param {File} file - File to upload
 * @param {string} uploadId - Upload ID from S3
 * @param {string} key - S3 key
 * @param {number} concurrentUploads - Number of concurrent uploads
 * @param {Function} onProgressCallback - Callback when there is upload progress
 * @returns {Promise<Array>} - List of uploaded parts
 */
export const uploadInParallel = async (
  file,
  uploadId,
  key,
  concurrentUploads = 3,
  onProgressCallback = null
) => {
  const chunkSize = getOptimalChunkSize();
  const totalParts = Math.ceil(file.size / chunkSize);

  // FIX 20260727-empty0 サイズ0／部品0件は「成功」にせず、S3を片付けて中断する
  if (!file || !file.size || totalParts === 0) {
    clearUploadState(file && file.name);
    await abortMultipartUpload(uploadId, key);
    throw new Error("EMPTY_FILE");
  }

  const partNumbers = [...Array(totalParts).keys()].map((i) => i + 1);

  // Initialize result array with enough elements
  const uploadedParts = new Array(totalParts).fill(null);

  // Get saved state (if any)
  const savedState = getUploadState(file.name);
  let completedParts = [];
  let simulatedProgress = 0;
  let intervalId = null;

  if (savedState && savedState.upload_id === uploadId) {
    completedParts = savedState.parts_completed || [];

    // Resume completed parts
    completedParts.forEach((part) => {
      uploadedParts[part.PartNumber - 1] = part;
    });

    // Update progress if there is callback
    if (onProgressCallback) {
      if (completedParts.length === 0) {
        intervalId = setInterval(() => {
          simulatedProgress += 1;
          onProgressCallback(simulatedProgress);
          // Stop simulated progress at 99 to prevent exceeding 100%
          // and ensure `isBackToRealProgress` condition can eventually be satisfied.
          if (simulatedProgress >= 85) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }, Math.max(totalParts * 5, 2000));
      } else {
        if (intervalId) clearInterval(intervalId);
        const progress = Math.round((completedParts.length / totalParts) * 100);
        onProgressCallback(progress);
      }
    }
  }

  // Filter out parts that are already uploaded
  const remainingParts = partNumbers.filter(
    (partNumber) =>
      !completedParts.some((part) => part.PartNumber === partNumber)
  );

  if (remainingParts.length === 0) {
    const resumedParts = uploadedParts.filter(Boolean);
    // FIX 20260727-empty0 部品が揃っていない状態で「完了」として返さない
    if (resumedParts.length === 0 || resumedParts.length !== totalParts) {
      clearUploadState(file.name);
      await abortMultipartUpload(uploadId, key);
      throw new Error("EMPTY_PARTS");
    }
    console.log("All parts already uploaded, resuming from saved state");
    return resumedParts;
  }

  // Create partsData for remaining parts
  const partsData = remainingParts.map((partNumber) => {
    const start = (partNumber - 1) * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    return {
      partNumber,
      blob: file.slice(start, end),
    };
  });

  // Counter for completed parts
  let completed = completedParts.length;
  const MAX_RETRIES = 1;

  const uploadSinglePart = async ({ partNumber, blob }, retryCount = 0) => {
    if (_abortRequested) throw new Error("ABORTED_BY_USER");
    try {
      const presignUrlRes = await multipartApiClient.get(
        `/akaire_feature/multipart_uploads/presign_part?upload_id=${uploadId}&key=${encodeURIComponent(
          key
        )}&part_number=${partNumber}`
      );
      const { url } = presignUrlRes.data;

      // Use Promise with XHR to track progress better
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        _activeXhrs.add(xhr);
        xhr.open("PUT", url);

        // Add timeout to avoid request hanging
        xhr.timeout = 180000; // 3 minutes timeout

        xhr.onload = () => {
          _activeXhrs.delete(xhr);
          if (xhr.status === 200) {
            const rawEtag = xhr.getResponseHeader("ETag");

            if (!rawEtag) {
              reject(
                new Error(
                  "ETag header is missing — check S3 CORS configuration"
                )
              );
              return;
            }

            const etag = rawEtag.replace(/"/g, "");
            const partData = { ETag: etag, PartNumber: partNumber };

            // Update array at the correct position
            uploadedParts[partNumber - 1] = partData;

            // Count completed parts
            completed++;

            // Update saved state
            const state = getUploadState(file.name);
            // FIX 20260901-samename 別便の記録に自分の部品を書き込まない
            if (state && state.upload_id === uploadId) {
              state.parts_completed = uploadedParts.filter(Boolean);
              saveUploadState(file.name, state);
            }

            // Update progress
            if (onProgressCallback) {
              const progress = Math.round((completed / totalParts) * 100);
              const isBackToRealProgress = progress >= simulatedProgress;
              if ((simulatedProgress >= 85 || isBackToRealProgress) && intervalId) {
                clearInterval(intervalId);
              }

              if (isBackToRealProgress) {
                onProgressCallback(progress);
              }
            }

            resolve(partData);
          } else {
            reject(
              new Error(`Failed to upload part ${partNumber}: ${xhr.status}`)
            );
          }
        };

        xhr.onerror = () => {
          _activeXhrs.delete(xhr);
          reject(new Error(`Network error for part ${partNumber}`));
        };
        xhr.ontimeout = () => {
          _activeXhrs.delete(xhr);
          reject(new Error(`Timeout uploading part ${partNumber}`));
        };
        xhr.send(blob);
      });
    } catch (error) {
      // Retry logic
      if (retryCount < MAX_RETRIES) {
        console.warn(
          `Retrying upload for part ${partNumber}, attempt ${retryCount + 1}`
        );
        // Exponential backoff
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, retryCount) * 1000)
        );
        return uploadSinglePart({ partNumber, blob }, retryCount + 1);
      }

      console.error(
        `Failed to upload part ${partNumber} after ${MAX_RETRIES} retries:`,
        error
      );
      throw error;
    }
  };

  // Process in parallel with semaphore pattern to control better
  const semaphore = {
    count: concurrentUploads,
    queue: [],

    async acquire() {
      if (this.count > 0) {
        this.count--;
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        this.queue.push(resolve);
      });
    },

    release() {
      if (this.queue.length > 0) {
        const resolve = this.queue.shift();
        resolve();
      } else {
        this.count++;
      }
    },
  };

  try {
    // Perform upload of remaining parts with semaphore
    await Promise.all(
      partsData.map(async (part) => {
        await semaphore.acquire();
        try {
          await uploadSinglePart(part);
        } finally {
          semaphore.release();
        }
      })
    );

    // Filter out only non-null elements
    return uploadedParts.filter(Boolean);
  } catch (error) {
    // Abort upload and throw error
    console.error("Error during parallel upload:", error);
    await abortMultipartUpload(uploadId, key);
    if (_abortRequested) {
      clearUploadState(file.name);
      throw new Error("ABORTED_BY_USER");
    }
    throw error;
  }
};

/**
 * Complete multipart upload
 * @param {string} uploadId - Upload ID from S3
 * @param {Array} parts - List of parts
 * @param {string} key - S3 key
 * @returns {Promise<{url: string}>} - URL of uploaded file
 */
export const completeMultipartUpload = async (uploadId, parts, key) => {
  // FIX 20260727-empty0 部品が空ならcompleteを送らずabortに切り替える（空XML→500の防止）
  const validParts = (parts || []).filter(
    (p) => p && p.ETag && p.PartNumber
  );
  if (validParts.length === 0) {
    await abortMultipartUpload(uploadId, key);
    throw new Error("EMPTY_PARTS");
  }

  try {
    const response = await multipartApiClient.post(
      "/akaire_feature/multipart_uploads/complete",
      {
        upload_id: uploadId,
        parts: validParts,
        key: key,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error completing multipart upload:", error);
    throw error;
  }
};

/**
 * Utility function to perform the entire multipart upload process
 * @param {File} file - File to upload
 * @param {Function} onProgressCallback - Callback when there is upload progress
 * @param {number} concurrentUploads - Number of concurrent uploads
 * @returns {Promise<{url: string}>} - URL of uploaded file
 */
export const performMultipartUpload = async (
  file,
  onProgressCallback = null,
  concurrentUploads = 3
) => {
  // FIX 20260901-samename2 送信中に印を押し直す timer
  let _aliveTimer = null;
  try {
    _abortRequested = false;

    // FIX 20260727-empty0 中身が空(0バイト)のファイルは initiate を呼ばずここで中止
    if (!file || file.size === 0) {
      if (file && file.name) clearUploadState(file.name);
      throw new Error("EMPTY_FILE");
    }

    // FIX 20260901-samename ★同名がまだ送信中なら、その伝票を片付けない
    const isUploadingNow = _isUploadingNow(file.name);
    _markUploading(file.name);

    // Check if there is saved upload state
    const savedState = getUploadState(file.name);
    let upload_id, key;

    const canAbortPrev = !isUploadingNow && !_isStateAlive(savedState);

    if (savedState && savedState.file_size === file.size && canAbortPrev) {
      // Abort any existing upload instead of resuming
      await abortMultipartUpload(savedState.upload_id, savedState.key);
      clearUploadState(file.name);
      console.log("Found existing upload state, aborting previous upload");

      // Initialize new upload
      const initResult = await initMultipartUpload(file, false);
      upload_id = initResult.upload_id;
      key = initResult.key;
    } else {
      // Initialize new upload
      const initResult = await initMultipartUpload(file, canAbortPrev);
      upload_id = initResult.upload_id;
      key = initResult.key;
    }

    // FIX 20260901-samename2 送信中は5秒ごとに「生きている印」を押し直す
    _aliveTimer = setInterval(() => {
      try {
        const st = getUploadState(file.name);
        if (st && st.upload_id === upload_id) saveUploadState(file.name, st);
      } catch (e) {}
    }, ALIVE_BEAT_MS);

    // Upload parts
    if (concurrentUploads == 1) {
      await sleep(5000);
    }

    const parts = await uploadInParallel(
      file,
      upload_id,
      key,
      concurrentUploads,
      onProgressCallback
    );

    // Complete multipart upload
    const result = await completeMultipartUpload(upload_id, parts, key);

    // Clear saved state after successful upload
    clearUploadState(file.name);

    return result;
  } catch (error) {
    console.error("Error during multipart upload process:", error);
    // FIX 20260727-keepstate 通信エラー等では中断記録を残す
    // （記録を消すとS3に残った未完了の部品を片付ける機会が失われるため。再開処理自体は現状abort+新規やり直し・別案件）
    // 記録を消すのは意味が無い時だけ：空ファイル／部品0件／会員が中止した時
    const failureMessage = (error && error.message) || "";
    const shouldClearState =
      _abortRequested ||
      failureMessage === "EMPTY_FILE" ||
      failureMessage === "EMPTY_PARTS" ||
      failureMessage === "ABORTED_BY_USER";
    if (shouldClearState && file && file.name) clearUploadState(file.name);
    throw error;
  } finally {
    // FIX 20260901-samename 送信中の印を必ず外す
    if (_aliveTimer) { clearInterval(_aliveTimer); _aliveTimer = null; }
    _unmarkUploading(file && file.name);
  }
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export default {
  initMultipartUpload,
  uploadInParallel,
  completeMultipartUpload,
  performMultipartUpload,
  abortMultipartUpload,
  getUploadState,
  requestMultipartAbort,
};
